import React, { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * Generic, server-driven data table built on the ui/table primitives + ui/pagination.
 *
 * Everything is CONTROLLED by the caller (sort/search/pagination state lives in
 * the page), because the Contracts tabs paginate, sort, and search server-side
 * against the live Cinch API. The search box debounces locally so we don't fire
 * a request per keystroke. Per-cell lazy loading (e.g. the Accounts Stripe/Status
 * columns that resolve from a separate enrichment query) is just a column
 * `render` that returns a <Skeleton/> until its data arrives — the table stays
 * generic.
 */

export interface DataTableColumn<T> {
  /** Sort key sent to the server as `ordering` (and the default field accessor). */
  key: string;
  header: string;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  numeric?: boolean;
  render?: (row: T) => React.ReactNode;
  className?: string;
}

export interface DataTableSort {
  key: string;
  dir: "asc" | "desc";
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  sort?: DataTableSort | null;
  onSortChange?: (sort: DataTableSort | null) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  pagination?: { limit: number; offset: number; count: number };
  onPageChange?: (offset: number) => void;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  /** Right-aligned toolbar content (e.g. a refresh button + "as of" stamp). */
  toolbar?: React.ReactNode;
  containerClassName?: string;
  /** Optional summary row(s) rendered in a semantic <tfoot> after the body.
   *  Pass a render function that gets the column list — typical use is a
   *  single roll-up <TableRow> with totals matching the column shape. */
  footer?: (columns: DataTableColumn<T>[]) => React.ReactNode;
  /** Expandable rows: provide the expanded content for a row and every row
   *  gains a leading chevron; clicking the row (or the chevron) toggles a
   *  full-width detail row beneath it. Multiple rows may be open at once.
   *  When `onRowClick` is ALSO provided, the row click keeps its meaning and
   *  only the chevron toggles. Zebra striping is disabled in expandable mode
   *  (detail rows would break nth-child parity). */
  renderExpanded?: (row: T) => React.ReactNode;
  /** Row keys expanded on first render (uncontrolled thereafter). */
  defaultExpandedKeys?: string[];
}

const SKELETON_ROWS = 8;

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  sort = null,
  onSortChange,
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  pagination,
  onPageChange,
  loading = false,
  error = null,
  emptyMessage = "No results.",
  onRowClick,
  selectedKey = null,
  toolbar,
  containerClassName = "max-h-[calc(100vh-18rem)]",
  footer,
  renderExpanded,
  defaultExpandedKeys,
}: DataTableProps<T>) {
  const expandable = renderExpanded != null;
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(defaultExpandedKeys ?? []),
  );
  function toggleExpanded(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  // The chevron column is extra; every colSpan must account for it.
  const colCount = columns.length + (expandable ? 1 : 0);
  // Local, debounced search so typing doesn't fire a request per keystroke.
  // Refs keep the effect deps to just `searchInput` while always seeing the
  // latest controlled value + callback.
  const [searchInput, setSearchInput] = useState(search ?? "");
  const controlledSearch = useRef(search ?? "");
  controlledSearch.current = search ?? "";
  const onSearchRef = useRef(onSearchChange);
  onSearchRef.current = onSearchChange;
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchInput !== controlledSearch.current) onSearchRef.current?.(searchInput);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  function toggleSort(key: string) {
    if (!onSortChange) return;
    if (sort?.key !== key) onSortChange({ key, dir: "asc" });
    else if (sort.dir === "asc") onSortChange({ key, dir: "desc" });
    else onSortChange(null);
  }
  const sortDir = (key: string) => (sort?.key === key ? sort.dir : null);

  const showSearch = onSearchChange != null;

  return (
    <div className="space-y-3">
      {(showSearch || toolbar) && (
        <div className="flex items-center justify-between gap-3">
          {showSearch ? (
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={searchPlaceholder}
              className="max-w-xs"
            />
          ) : (
            <span />
          )}
          {toolbar ?? null}
        </div>
      )}

      <div className="overflow-hidden rounded-md border">
        <Table containerClassName={containerClassName}>
          <TableHeader>
            <TableRow>
              {expandable && <TableHead className="w-8" aria-label="Expand" />}
              {columns.map((c) => (
                <TableHead
                  key={c.key}
                  align={c.align ?? (c.numeric ? "right" : "left")}
                  sortable={Boolean(c.sortable && onSortChange)}
                  sortDirection={c.sortable ? sortDir(c.key) : undefined}
                  onSort={c.sortable ? () => toggleSort(c.key) : undefined}
                >
                  {c.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody zebra={!expandable}>
            {error ? (
              <TableEmpty colSpan={colCount}>
                <span className="text-destructive">{error}</span>
              </TableEmpty>
            ) : loading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {expandable && <TableCell className="w-8" />}
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align} numeric={c.numeric}>
                      <Skeleton className="h-4 w-full max-w-[8rem]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableEmpty colSpan={colCount}>{emptyMessage}</TableEmpty>
            ) : (
              rows.map((row) => {
                const key = rowKey(row);
                const isOpen = expandable && expanded.has(key);
                // Row click keeps caller semantics when provided; otherwise
                // in expandable mode the whole row is the toggle target.
                const rowClick = onRowClick
                  ? () => onRowClick(row)
                  : expandable
                    ? () => toggleExpanded(key)
                    : undefined;
                return (
                  <React.Fragment key={key}>
                    <TableRow
                      clickable={rowClick != null}
                      selected={selectedKey === key}
                      onClick={rowClick}
                      aria-expanded={expandable ? isOpen : undefined}
                    >
                      {expandable && (
                        <TableCell className="w-8 pr-0">
                          <button
                            type="button"
                            aria-label={isOpen ? "Collapse row" : "Expand row"}
                            aria-expanded={isOpen}
                            className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(key);
                            }}
                          >
                            <ChevronRight
                              className={cnChevron(isOpen)}
                              aria-hidden
                            />
                          </button>
                        </TableCell>
                      )}
                      {columns.map((c) => (
                        <TableCell
                          key={c.key}
                          align={c.align}
                          numeric={c.numeric}
                          className={c.className}
                        >
                          {c.render ? c.render(row) : defaultCell(row, c.key)}
                        </TableCell>
                      ))}
                    </TableRow>
                    {isOpen && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell
                          colSpan={colCount}
                          className="bg-muted/20 p-0"
                        >
                          <div className="border-l-2 border-power/40 px-4 py-3">
                            {renderExpanded(row)}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
          {footer && rows.length > 0 && !loading && !error ? (
            <TableFooter>{footer(columns)}</TableFooter>
          ) : null}
        </Table>
      </div>

      {pagination && onPageChange ? (
        <Pagination
          limit={pagination.limit}
          offset={pagination.offset}
          count={pagination.count}
          onPageChange={onPageChange}
        />
      ) : null}
    </div>
  );
}

function defaultCell<T>(row: T, key: string): React.ReactNode {
  const v = (row as Record<string, unknown>)[key];
  if (v == null || v === "") return "—";
  return String(v);
}

function cnChevron(open: boolean): string {
  return `h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`;
}
